import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Badge, Box, styled, Typography } from '@mui/material';

import {
  NARROW_PAGE_QUERY,
  onPhoneOrNarrowPage,
} from '../style/phone-page-scroll';
import { useTheme } from '../styles/styles';

type TabOptionBase = {
  label: string;
  showIndicator?: boolean;
};

// 'soft': the employee header's tabs.
type TabsVariant = 'filled' | 'soft';

type RouteTabsProps =
  | {
      firstTabAsDefault?: boolean;
      updateUrlOnChange?: true;
      options: Array<TabOptionBase & { to: string }>;
      matchRouteKey: string;
      searchParams?: string;
      onClick?: (tab: string) => void;
      showIndicatorOnActiveTab?: boolean;
      variant?: TabsVariant;
    }
  | {
      firstTabAsDefault?: boolean;
      updateUrlOnChange: false;
      options: Array<TabOptionBase & { key: string }>;
      matchRouteKey: string;
      searchParams?: string;
      onClick?: (tab: string) => void;
      showIndicatorOnActiveTab?: boolean;
      variant?: TabsVariant;
    };

export const RouteTabs = memo((props: RouteTabsProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    matchRouteKey,
    options,
    onClick,
    searchParams = '',
    showIndicatorOnActiveTab = false,
    firstTabAsDefault = false,
    variant = 'filled',
  } = props;
  const soft = variant === 'soft';

  const updateUrlOnChange = props.updateUrlOnChange !== false;

  const suffix = useMemo(
    () => (searchParams ? `?${searchParams}` : ''),
    [searchParams]
  );

  const setActiveTab = useCallback(
    (value: string) => {
      if (updateUrlOnChange) navigate(value + suffix);
      onClick?.(value);
    },
    [navigate, suffix, updateUrlOnChange, onClick]
  );

  useEffect(() => {
    if (!options?.length || !firstTabAsDefault || !updateUrlOnChange) return;
    const typedOptions = options as Array<TabOptionBase & { to: string }>;
    const tabPaths = typedOptions
      .map((item) => item.to)
      .filter((p): p is string => Boolean(p));

    const hasTabInPath = tabPaths.some((tabPath) =>
      location.pathname.includes(`/${tabPath}`)
    );

    if (!hasTabInPath) {
      const firstWithPath = typedOptions.find((item) => item.to);

      if (firstWithPath) {
        navigate(firstWithPath.to + `?${searchParams}`);
        if (onClick) onClick(firstWithPath.to);
      }
    }
  }, [location.pathname, options, onClick]);
  const theme = useTheme();

  const activeTabRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [matchRouteKey]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: soft ? '2px' : '8px',
        ...(soft && {
          display: { xs: 'flex', sm: 'inline-flex' },
          maxWidth: '100%',
          padding: '3px',
          borderRadius: '10px',
          backgroundColor: theme.custom?.stepBg,
          ...onPhoneOrNarrowPage({ display: 'flex', flex: '1 1 100%' }),
        }),
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {options.map((item, index) => {
        const tabValue = updateUrlOnChange
          ? (item as any).to
          : (item as any).key;

        const isActive = matchRouteKey === tabValue;

        const shouldShowIndicator =
          Boolean(item.showIndicator) &&
          (showIndicatorOnActiveTab || !isActive);

        return (
          <Box
            key={index}
            ref={isActive ? activeTabRef : undefined}
            onClick={() => setActiveTab(tabValue)}
            sx={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
              ...(soft && {
                flex: { xs: '1 0 auto', sm: '0 0 auto' },
                textAlign: 'center',
                boxShadow: isActive
                  ? theme.custom?.segmentActiveShadow
                  : 'none',
              }),
              borderRadius: soft ? '8px' : '10px',
              backgroundColor: isActive
                ? soft
                  ? theme.background.white
                  : theme.colors.primary.main
                : soft
                ? 'transparent'
                : 'white',
              padding: soft
                ? { xs: '7px 8px', sm: '7px 16px' }
                : '10px 24px 9px 24px',
              ...(soft && {
                [NARROW_PAGE_QUERY]: {
                  flex: '1 0 auto',
                  padding: '7px 8px',
                },
              }),
              transition: 'all 0.2s ease',
              '&:hover': soft
                ? {
                    '& .MuiTypography-root': { color: theme.custom?.brandNavy },
                  }
                : {
                    backgroundColor: isActive
                      ? undefined
                      : theme.background.lightGrey,
                  },
            }}
          >
            {shouldShowIndicator && soft ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <Label isActive={isActive} soft={soft}>
                  {item.label}
                </Label>
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    width: 8,
                    height: 8,
                    flexShrink: 0,
                    borderRadius: '50%',
                    backgroundColor: theme.custom?.pendingDot,
                  }}
                />
              </Box>
            ) : shouldShowIndicator ? (
              <Badge
                variant="dot"
                color="primary"
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                  '& .MuiBadge-badge': {
                    top: 6,
                    right: -6,
                  },
                }}
              >
                <Label isActive={isActive} soft={soft}>
                  {item.label}
                </Label>
              </Badge>
            ) : (
              <Label isActive={isActive} soft={soft}>
                {item.label}
              </Label>
            )}
          </Box>
        );
      })}
    </Box>
  );
});

const Label = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'soft',
})<{ isActive: boolean; soft?: boolean }>(({ theme, isActive, soft }) =>
  soft
    ? {
        cursor: 'pointer',
        color: isActive
          ? theme.custom?.brandNavy
          : theme.custom?.shellSectionTabText,
        fontSize: '14.5px',
        fontWeight: isActive ? 600 : 500,
        lineHeight: '20.3px',
        [theme.breakpoints.down('sm')]: { fontSize: '13.5px' },
        [NARROW_PAGE_QUERY]: { fontSize: '13.5px' },
      }
    : {
        cursor: 'pointer',
        color: isActive ? 'white' : theme.text.inactive,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '-0.15px',
      }
);
